import { useTranslation } from 'react-i18next';
// Base UI's Provider only — it supplies the instant open/close timing shared by
// every popup on the strip. The popups themselves go through ui/Tooltip.
import { Tooltip } from '@base-ui/react/tooltip';
import { Tooltip as UiTooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import type { ProjectMilestoneStrip } from '@interfaces/IProject';
import { FlagIcon } from '@utils/icons';
import { formatLongDate, formatMediumDate, APP_DEFAULT_TZ, currentLang } from '@utils/dateUtils';
import type { IMilestoneSummary } from '../MilestoneHighlights/summarizeMilestones';
import { SectionHead } from '../SectionHead/SectionHead';
import styles from './styles.module.css';

type IMilestoneStripProps = { strip: ProjectMilestoneStrip; summary: IMilestoneSummary; unavailable?: boolean };

const clamp = (v: number) => Math.min(100, Math.max(0, v));

/** Dot radius (px). Edge dots (0% / 100%) are inset by this much so the
 *  first/last milestone aren't half-clipped at the rail ends and stay hoverable. */
const DOT_R = 5;

/** Inset the dot's left so a 0%/100% milestone sits fully inside the rail. */
function dotLeft(positionPct: number): string {
  return `clamp(${DOT_R}px, ${clamp(positionPct)}%, calc(100% - ${DOT_R}px))`;
}

export function MilestoneStrip({ strip, summary, unavailable }: IMilestoneStripProps) {
  const { t } = useTranslation();
  const lang = currentLang();
  const { milestones, nowPct, currentDelayed } = strip;
  const title = t('portfolio.card.milestones');

  // Dates (today marker + each milestone day) render via the shared JST-aware
  // dateUtils formatter — its bare-YYYY-MM-DD handling anchors the day IN the
  // tz, so the calendar day is correct regardless of the browser's timezone
  // (no off-by-one). The date is data (Intl-formatted), so no i18n key is needed.

  // No schedule data returned → empty rail + "data unavailable" caption below.
  // No fallback positioning.
  if (unavailable) {
    return (
      <div className={styles.wrap}>
        <SectionHead title={title} />
        <div className={styles.track} aria-hidden>
          <div className={styles.rail} />
        </div>
        <span className={styles.caption} data-testid="data-unavailable">
          {t('portfolio.card.dataUnavailable')}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <SectionHead title={title}>
        <span className={styles.count} data-testid="milestone-count">
          <b>{summary.completed} / {summary.total}</b> {t('portfolio.card.milestonesCompleted')}
        </span>
      </SectionHead>
      {/* Instant, styled tooltips (Base UI) — names on dots, timeline % on the bar. */}
      <Tooltip.Provider delay={0} closeDelay={0}>
        <div className={styles.track}>
          <div className={styles.rail} />
          <div
            className={styles.fill}
            data-testid="milestone-now-fill"
            data-delayed={String(currentDelayed)}
            style={{ width: `${clamp(nowPct)}%` }}
          />

          {/* Hover anywhere on the bar → how far along the timeline "now" is. */}
          <UiTooltip
            variant="marker"
            delay={0}
            content={
              <TooltipContent
                title={t('portfolio.card.todayTooltip', {
                  date: formatLongDate(new Date(), lang, APP_DEFAULT_TZ),
                })}
                lines={[t('portfolio.card.timelineProgress', { pct: Math.round(clamp(nowPct)) })]}
              />
            }
          >
            <div className={styles.hoverLayer} data-testid="milestone-bar-hover" />
          </UiTooltip>

          {/* Each milestone dot → its name on hover. Key on name+position since
              milestone names aren't guaranteed unique within a schedule. */}
          {milestones.map((m) => (
            <UiTooltip
              key={`${m.name}@${m.positionPct}`}
              variant="marker"
              delay={0}
              content={
                <TooltipContent
                  title={m.name}
                  lines={[formatLongDate(m.dateIso, lang, APP_DEFAULT_TZ)]}
                />
              }
            >
              <span
                className={styles.dot}
                data-testid="milestone-dot"
                data-state={m.state}
                style={{ left: dotLeft(m.positionPct) }}
              />
            </UiTooltip>
          ))}
        </div>
      </Tooltip.Provider>
      {summary.range ? (
        <div className={styles.ends} data-testid="milestone-ends">
          <span>{formatMediumDate(summary.range.startIso, lang, APP_DEFAULT_TZ)}</span>
          <span className={styles.end}>
            <FlagIcon size={11} />
            {formatMediumDate(summary.range.endIso, lang, APP_DEFAULT_TZ)}
          </span>
        </div>
      ) : null}
    </div>
  );
}
